'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { Brain, Check, Copy, MessageSquare, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import ActionCard from './ActionCard'
import { parseMarkdown, splitActionCard } from './Message'
import { markAsEngaged, type ChatContext } from '@/lib/chat-context'
import AskShell, {
  useRotatingPlaceholder,
  type AskShellHandle,
  type AskShellMode,
} from './AskShell'
import {
  useVectorChat,
  usePrefersReducedMotion,
  MAX_MESSAGE_LENGTH,
  type ChatMessage,
  type ChatMode,
} from './useVectorChat'

const MINIMIZED_KEY = 'pm_ask_pixelmojo_minimized'

// Rotating placeholder prompts (initial state) double as the sales suggestions
const EXAMPLE_QUESTIONS = [
  'How visible is my brand in ChatGPT?',
  'Which Pixelmojo product should I start with?',
  'What prevents AI systems from citing my website?',
  'Can Pixelmojo build a custom AI product?',
  'How is Radar different from traditional SEO tools?',
]

const TRIVIA_QUESTIONS = [
  "Give me a mind-blowing AI fact I probably don't know.",
  "What's a design psychology trick that most websites miss?",
  'Tell me the weirdest origin story in tech history.',
]

interface AskPixelmojoProps {
  chatContext: ChatContext | null
  isVisible: boolean
}

/**
 * AskPixelmojo — Vector's content + business logic rendered through the
 * shared AskShell. Layout, states, transitions, and accessibility live in
 * AskShell; data concerns live in useVectorChat.
 */
export default function AskPixelmojo({
  chatContext,
  isVisible,
}: AskPixelmojoProps) {
  const [chatMode, setChatMode] = useState<ChatMode>('sales')
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null)
  const [shellMode, setShellMode] = useState<AskShellMode>('initial')
  const [draftMirror, setDraftMirror] = useState('')
  // Typewriter waits for the shell's on-load entrance to settle
  const [introDone, setIntroDone] = useState(false)

  const shellRef = useRef<AskShellHandle>(null)
  const prefersReducedMotion = usePrefersReducedMotion()

  const {
    ensureSession,
    messages,
    send,
    retry,
    isLoading,
    loadingMessage,
    isLimitReached,
    rateLimitCooldown,
    sessionExpired,
    countdown,
    sendingMessageId,
    typewriterMessageId,
    displayedContent,
  } = useVectorChat({ chatContext, chatMode })

  const placeholder = useRotatingPlaceholder(
    EXAMPLE_QUESTIONS,
    shellMode === 'initial' && introDone,
    prefersReducedMotion
  )

  const handleIntroComplete = useCallback(() => setIntroDone(true), [])

  const engage = useCallback(() => {
    markAsEngaged()
    void ensureSession()
  }, [ensureSession])

  // Programmatic open (Hero CTA, Vector video placeholder)
  useEffect(() => {
    const handler = () => shellRef.current?.restore()
    window.addEventListener('openChatWidget', handler)
    return () => window.removeEventListener('openChatWidget', handler)
  }, [])

  const copyMessage = useCallback(
    async (content: string, messageId: string) => {
      try {
        await navigator.clipboard.writeText(content)
        setCopiedMessageId(messageId)
        setTimeout(() => setCopiedMessageId(null), 2000)
      } catch (err) {
        console.error('Failed to copy:', err)
      }
    },
    []
  )

  const initiallyMinimized =
    typeof window !== 'undefined' &&
    // Mobile starts as the compact pill instead of auto-opening the search
    // bar. A tap still restores the existing suggestions / answer sheet.
    (window.matchMedia('(max-width: 767px)').matches ||
      // 'true' = pill, 'docked' = wide docked bar; the shell reads which
      sessionStorage.getItem(MINIMIZED_KEY) !== null ||
      sessionStorage.getItem('pixelmojo_greeting_dismissed') === 'true')

  const suggestions =
    chatMode === 'sales' ? EXAMPLE_QUESTIONS : TRIVIA_QUESTIONS
  const canSubmit =
    !isLoading &&
    !isLimitReached &&
    !(rateLimitCooldown !== null && rateLimitCooldown > 0)

  return (
    <AskShell
      ref={shellRef}
      isVisible={isVisible}
      title='Ask Vector'
      subtitle='Pixelmojo AI'
      headerExtras={
        chatMode === 'trivia' ? (
          <span className='rounded-full bg-accent-pink/10 px-2 py-0.5 text-[10px] font-medium leading-none text-accent-pink'>
            Trivia
          </span>
        ) : undefined
      }
      initialPlaceholder={`Try asking: ${placeholder}`}
      suggestionsPlaceholder='Ask anything about Pixelmojo'
      answerPlaceholder={
        isLimitReached ? 'Message limit reached' : 'Ask another question'
      }
      inputAriaLabel='Ask Vector a question'
      suggestions={suggestions}
      suggestionsHeader={
        <>
          {/* Mode toggle (sales / trivia) */}
          <div className='mb-2 flex items-center gap-1 rounded-lg bg-muted/50 p-0.5'>
            <button
              onClick={() => setChatMode('sales')}
              className={cn(
                'flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                chatMode === 'sales'
                  ? 'border border-line-strong bg-card text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <MessageSquare className='h-3 w-3' />
              Ask Vector
            </button>
            <button
              onClick={() => setChatMode('trivia')}
              className={cn(
                'flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                chatMode === 'trivia'
                  ? 'border border-line-strong bg-card text-accent-pink'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Brain className='h-3 w-3' />
              AI Trivia
            </button>
          </div>
          <p className='mb-1 px-1 text-xs text-muted-foreground'>
            {chatMode === 'sales'
              ? "Vector, Pixelmojo's AI. Ask about our products, AI visibility, or your project."
              : 'Trivia mode. Test your AI and design knowledge.'}
          </p>
        </>
      }
      canSubmit={canSubmit}
      inputDisabled={
        isLimitReached || (rateLimitCooldown !== null && rateLimitCooldown > 0)
      }
      maxLength={MAX_MESSAGE_LENGTH}
      onSubmit={question => void send(question)}
      onDraftChange={setDraftMirror}
      answerInputHint={
        <>
          {rateLimitCooldown !== null && rateLimitCooldown > 0 && (
            <p className='mt-2 text-xs text-muted-foreground'>
              Please wait {rateLimitCooldown} second
              {rateLimitCooldown !== 1 ? 's' : ''} before sending another
              message.
            </p>
          )}
          {draftMirror.length >= MAX_MESSAGE_LENGTH && (
            <p className='mt-2 text-xs text-destructive'>
              Message is too long. Maximum {MAX_MESSAGE_LENGTH} characters.
            </p>
          )}
        </>
      }
      hasConversation={messages.length > 0}
      storageKey={MINIMIZED_KEY}
      initiallyMinimized={initiallyMinimized}
      onModeChange={setShellMode}
      onFirstOpen={engage}
      onIntroComplete={handleIntroComplete}
      enableCmdK
      answerFooter={
        <>
          {/* Limit-reached CTA (existing behavior) */}
          {isLimitReached && (
            <div className='border-t border-border px-4 py-4 text-center'>
              <p className='mb-3 text-sm font-medium text-foreground'>
                Want to continue chatting?
              </p>
              <a
                href='https://www.pixelmojo.io/contact-us'
                target='_blank'
                rel='noopener noreferrer'
                className='inline-block rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90'
              >
                Contact Us to Continue
              </a>
            </div>
          )}

          {/* Disclaimer (existing copy) */}
          <div className='border-t border-border px-4 py-2.5'>
            <p className='text-center text-xs text-muted-foreground'>
              Vector is an AI agent and can make mistakes. This chat is subject
              to our{' '}
              <a
                href='https://www.pixelmojo.io/privacy-policy'
                target='_blank'
                rel='noopener noreferrer'
                className='underline transition-colors hover:text-primary'
              >
                Privacy Policy
              </a>
            </p>
          </div>
        </>
      }
      scrollKey={`${messages.length}|${displayedContent.length}|${isLoading}`}
    >
      {sessionExpired ? (
        <div className='flex items-center justify-center py-16'>
          <div className='max-w-md space-y-4 text-center'>
            <p className='text-lg font-semibold text-foreground'>
              Session expired
            </p>
            <p className='text-sm text-muted-foreground'>
              Your chat session has expired. The page will refresh automatically
              in {countdown} seconds.
            </p>
            <button
              onClick={() => window.location.reload()}
              className='rounded-xl bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
            >
              Refresh now
            </button>
          </div>
        </div>
      ) : (
        <>
          {messages.length === 0 && !isLoading && (
            <p className='text-sm text-muted-foreground'>
              Ask a question to get started.
            </p>
          )}
          {messages.map((message, index) =>
            message.role === 'user' ? (
              <QuestionBlock
                key={message.id || index}
                message={message}
                isSending={message.id === sendingMessageId}
              />
            ) : (
              <AnswerBlock
                key={message.id || index}
                message={message}
                content={
                  message.id === typewriterMessageId
                    ? displayedContent
                    : message.content
                }
                copied={copiedMessageId === message.id}
                onCopy={() =>
                  message.id && copyMessage(message.content, message.id)
                }
                onRetry={() => retry(index)}
              />
            )
          )}
          {isLoading && (
            <div className='flex items-center gap-3'>
              <div className='flex gap-1'>
                <span className='h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:0ms] motion-reduce:animate-none' />
                <span className='h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:150ms] motion-reduce:animate-none' />
                <span className='h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:300ms] motion-reduce:animate-none' />
              </div>
              <span className='text-sm text-muted-foreground'>
                {loadingMessage}
              </span>
            </div>
          )}
        </>
      )}
    </AskShell>
  )
}

function QuestionBlock({
  message,
  isSending,
}: {
  message: ChatMessage
  isSending: boolean
}) {
  return (
    <div>
      <span className='text-[11px] font-medium uppercase tracking-wider text-muted-foreground'>
        You asked
      </span>
      <p className='mt-1 text-base font-semibold leading-snug text-foreground md:text-lg'>
        {message.content}
      </p>
      {isSending && (
        <span className='mt-1 inline-flex items-center gap-1.5 text-xs text-muted-foreground'>
          <span className='h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground motion-reduce:animate-none' />
          Sending...
        </span>
      )}
    </div>
  )
}

function AnswerBlock({
  message,
  content,
  copied,
  onCopy,
  onRetry,
}: {
  message: ChatMessage
  content: string
  copied: boolean
  onCopy: () => void
  onRetry: () => void
}) {
  const { text, cardType, cardUrl } = splitActionCard(content)

  if (message.error) {
    return (
      <div className='rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3'>
        <p className='text-sm text-foreground'>{text}</p>
        {message.retryable && (
          <button
            onClick={onRetry}
            className='mt-2 flex items-center gap-1.5 text-xs text-destructive transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive'
            aria-label='Retry message'
          >
            <RotateCcw className='h-3 w-3' />
            Retry
          </button>
        )}
      </div>
    )
  }

  return (
    <div className='group'>
      {text && (
        <div className='whitespace-pre-wrap text-sm leading-relaxed text-foreground md:text-base'>
          {parseMarkdown(text)}
        </div>
      )}
      {cardType && (
        <div className='mt-4 max-w-md'>
          <ActionCard type={cardType} url={cardUrl} />
        </div>
      )}
      {message.id && (
        <div className='mt-2 flex items-center gap-2 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100'>
          <button
            onClick={onCopy}
            className='flex items-center gap-1 rounded p-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
            aria-label='Copy answer'
            title='Copy answer'
          >
            {copied ? (
              <Check className='h-3.5 w-3.5 text-growth' />
            ) : (
              <Copy className='h-3.5 w-3.5' />
            )}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      )}
    </div>
  )
}
